/**
 * CORE-F — Skill Heatmap
 *
 * Renders demonstrated capability per category, alongside four other
 * dimensions that are deliberately NOT merged into it.
 *
 * What changed and why
 * --------------------
 * The previous build computed `proficiency?.proficiency_score || 0` and
 * rendered the result. A category the technician had never worked came out
 * as `0`, coloured `bg-destructive/30`, labelled "Novice". A failed fetch
 * did the same thing to every category at once. The screen was making a
 * confident negative claim about a person out of an absence of data.
 *
 * Now every value arrives as a `DimensionState`, and the three non-value
 * states — loading, unavailable, no evidence — have no number to render.
 * They are drawn as neutral dashed cells with their own words and their own
 * glyphs, visually distinct from the capability ramp rather than sitting at
 * the bottom of it.
 *
 * Provenance is kept visible rather than implied. Diagnostic competency,
 * training readiness, efficiency and completed-work volume each keep their
 * own owner, their own unit and their own wording; none of them is blended
 * into the proficiency colour. Training completed is not work demonstrated,
 * and this surface must never let the two read as the same claim.
 *
 * Accessibility: colour is never the only channel. Every cell carries a
 * glyph and a word as well, is reachable by keyboard, and exposes the full
 * multi-dimension detail through a focusable control rather than a
 * hover-only `title`.
 */

import { useState } from 'react';
import { useCategoryAuthority, type CategoryAuthority } from '@/hooks/passport/useCategoryAuthority';
import { CategoryAuthorityExplanation } from '@/components/passport/CategoryAuthorityExplanation';
import { PassportProvenanceBadge } from '@/components/passport/PassportProvenanceBadge';
import { proficiencyFactGroup } from '@/lib/passport/proficiencyProvenance';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Grid3X3, Info } from 'lucide-react';
import { CATEGORY_GROUPS, getCategoryIcon } from '@/utils/technicianPassportUtils';
import { cn } from '@/lib/utils';
import { useSkillHeatmapData } from '@/hooks/passport/useSkillHeatmapData';
import {
  BAND_PRESENTATION,
  DIMENSION_LABEL,
  DIVERGENCE_PRESENTATION,
  EVIDENCE_PRESENTATION,
  RECENCY_PRESENTATION,
  STATE_PRESENTATION,
  describeCell,
  describeDimensionProvenance,
  describeSourceFailures,
  formatDimensionValue,
  formatRecordedConfidence,
  formatSampleCount,
  formatSchedulingContext,
  stateKeyFor,
} from '@/lib/passport/skillHeatmap/display';
import {
  HEATMAP_DIMENSIONS,
  PROVENANCE_EXPLANATION,
  PROVENANCE_LABEL,
  PROVENANCE_LIMITATION,
  PORTABLE_CLASS_LABEL,
  PORTABLE_CLASS_MARK,
  type HeatmapCell,
  type HeatmapDimensionKey,
  type HeatmapSourceStates,
} from '@/lib/passport/skillHeatmap/types';

export interface PassportSkillHeatmapProps {
  technicianProfileId: string | undefined;
  /** The verified live `technicians.id`, when the surface has one. */
  liveShopTechnicianId?: string | null;
  /** The verified live proficiency overlay, when the surface is showing one. */
  liveProficiencies?: Record<string, number> | null;
  /** Injected for deterministic recency under test. */
  nowIso?: string;
}

/** Shorter labels so narrow cells stay readable. Display only. */
const SHORT_NAMES: Record<string, string> = {
  'Steering & Suspension': 'Suspension',
  'Heating Ventilation A/C': 'HVAC',
  'EV / Hybrid High Voltage': 'EV/Hybrid',
  'Engine Mechanical': 'Engine',
  'Engine Performance': 'Performance',
  'Starting & Charging': 'Charging',
  'Body & Frame': 'Body',
};

/**
 * Which dimensions failed to load outright.
 *
 * Surfaced as a banner so a reader is told the map is incomplete. Without
 * it, a person cannot tell whether a gap is their record or our outage —
 * and would reasonably assume the former.
 */
function failedDimensions(
  sources: HeatmapSourceStates,
): HeatmapDimensionKey[] {
  const failed: HeatmapDimensionKey[] = [];
  if (sources.proficiency === 'error') failed.push('proficiency');
  if (sources.diagnostic === 'error') failed.push('diagnostic');
  if (sources.readiness === 'error') failed.push('readiness');
  if (sources.jobStats === 'error') {
    failed.push('efficiency');
    failed.push('volume');
  }
  return failed;
}

function CellDetail({
  cell,
  authorityByCategory,
  hasShopContext,
}: {
  cell: HeatmapCell;
  authorityByCategory?: Record<string, CategoryAuthority>;
  hasShopContext: boolean;
}) {
  const divergence = DIVERGENCE_PRESENTATION[cell.divergence];
  return (
    <div className="space-y-3 text-xs">
      <div>
        <p className="font-semibold text-sm">{cell.category}</p>
        <p className="text-muted-foreground" data-testid="heatmap-detail-provenance">
          {PROVENANCE_LABEL.portable_record}
        </p>
        <p className="text-[11px] text-muted-foreground mt-1">
          {PROVENANCE_EXPLANATION.portable_record}
        </p>
        {/*
          Said plainly about the figure on the tile. `technician_proficiencies`
          carries no source column, so this number's origin genuinely cannot be
          told — and the tile's band is computed from THIS value, not from the
          scheduling authority below. Narrowed from "the stored record" to "the
          portable record" because the scheduling side now does carry a source.
        */}
        {/* Core-100 R4: the portable value states its recorded class; the limitation is said only when the class
            genuinely was not recorded. */}
        {cell.portableClass && (
          <p className="text-[11px] mt-1 font-medium" data-testid="heatmap-detail-portable-class" data-class={cell.portableClass}>
            {PORTABLE_CLASS_LABEL[cell.portableClass]}
            {/* Core-100 R5 (CORE-C F/Prov): the approved badge for the class, where the class has approved copy. */}
            {proficiencyFactGroup(cell.portableClass) && (
              <PassportProvenanceBadge
                factGroupId={proficiencyFactGroup(cell.portableClass) as string}
                className="ml-2 align-middle"
              />
            )}
          </p>
        )}
        {cell.portableClass === 'unrecorded' && (
          <p
            className="text-[11px] text-muted-foreground mt-1 italic"
            data-testid="heatmap-detail-provenance-limitation"
          >
            {PROVENANCE_LIMITATION}
          </p>
        )}
        {/*
          CORE-E TRUTH, from the canonical authority.

          This used to say "CORE-E stores no provenance field, so neither
          this screen nor anyone reading it can tell a manual baseline from
          a synced shop value from derived evidence." That was honest when
          it was written and is false now: 20261122120000 gave CORE-E an
          authority with a baseline, its class, an evidence count, a
          confidence and a defect adjustment, plus a durable evidence record
          that carries reversal.

          What follows explains the score SCHEDULING consumes, read from
          `technician_category_authority` — not the portable snapshot above,
          which is a different fact about the same person.
        */}
        <div className="mt-2 border-t border-dashed border-muted-foreground/30 pt-2">
          <CategoryAuthorityExplanation
            categoryKey={cell.category}
            data={authorityByCategory?.[cell.category]}
            hasNoShopContext={!hasShopContext}
          />
        </div>
      </div>

      {/*
        CORE-E's two stores, side by side. They are never averaged and
        neither is silently preferred: the reader is shown both figures and
        told when they disagree, because being scheduled on a number that no
        longer matches your demonstrated record is a fact about you that you
        are entitled to see.
      */}
      {divergence && (
        <div
          className="rounded-md border border-dashed border-muted-foreground/40 p-2 space-y-1"
          data-testid="heatmap-detail-divergence"
          data-divergence={cell.divergence}
        >
          <p className="font-medium">{divergence.label}</p>
          <div className="flex gap-4">
            <span data-testid="heatmap-detail-portable">
              <span className="text-muted-foreground">Portable: </span>
              {formatDimensionValue('proficiency', cell.portable)}
            </span>
            <span data-testid="heatmap-detail-scheduling">
              <span className="text-muted-foreground">Scheduling: </span>
              {formatDimensionValue('proficiency', cell.scheduling)}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {divergence.meaning}
          </p>
        </div>
      )}

      <dl className="space-y-2">
        {HEATMAP_DIMENSIONS.map((key) => {
          const state = cell[key];
          const stateKey = stateKeyFor(state);
          return (
            <div key={key} data-testid={`heatmap-detail-${key}`}>
              <dt className="font-medium">{DIMENSION_LABEL[key]}</dt>
              <dd
                className={cn(
                  'tabular-nums',
                  stateKey ? 'text-muted-foreground italic' : 'font-semibold',
                )}
              >
                {formatDimensionValue(key, state)}
              </dd>
              <dd className="text-[11px] text-muted-foreground">
                {stateKey
                  ? STATE_PRESENTATION[stateKey].meaning
                  : describeDimensionProvenance(key)}
              </dd>
              {key === 'efficiency' && cell.efficiencyPartial && (
                <dd
                  className="text-[11px] text-muted-foreground italic"
                  data-testid="heatmap-detail-efficiency-partial"
                >
                  Partial — some completed work in this category could not
                  contribute to this figure, so it does not cover all of it.
                </dd>
              )}
            </div>
          );
        })}
      </dl>

      <div className="border-t border-border pt-2 space-y-1">
        {/*
          The recorded numbers themselves, before any banding. "Moderate
          evidence" does not tell somebody whether four jobs or nine sit
          behind their rating; the band is an interpretation offered
          alongside the count, not instead of it.
        */}
        <p data-testid="heatmap-detail-sample">
          <span className="font-medium">Evidence sample: </span>
          {formatSampleCount(cell.sampleSize)}
        </p>
        <p data-testid="heatmap-detail-confidence">
          <span className="font-medium">Recorded confidence: </span>
          {formatRecordedConfidence(cell.confidence)}
        </p>
        <p data-testid="heatmap-detail-evidence">
          <span className="font-medium">Evidence: </span>
          {EVIDENCE_PRESENTATION[cell.evidence].label} —{' '}
          <span className="text-muted-foreground">
            {EVIDENCE_PRESENTATION[cell.evidence].meaning}
          </span>
        </p>
        <p data-testid="heatmap-detail-recency">
          <span className="font-medium">Last updated: </span>
          {RECENCY_PRESENTATION[cell.recency].label} —{' '}
          <span className="text-muted-foreground">
            {RECENCY_PRESENTATION[cell.recency].meaning}
          </span>
        </p>
      </div>
    </div>
  );
}

function HeatmapTile({
  cell,
  authorityByCategory,
  hasShopContext,
}: {
  cell: HeatmapCell;
  authorityByCategory?: Record<string, CategoryAuthority>;
  hasShopContext: boolean;
}) {
  const [open, setOpen] = useState(false);
  const Icon = getCategoryIcon(cell.category);
  const stateKey = stateKeyFor(cell.proficiency);
  const band = BAND_PRESENTATION[cell.band];
  const schedulingContext = formatSchedulingContext(cell.scheduling);

  // A non-value state takes the neutral presentation, never a rung on the
  // capability ramp. This is the line the old implementation crossed.
  const presentation = stateKey ? STATE_PRESENTATION[stateKey] : band;

  const primary =
    cell.proficiency.status === 'known'
      ? String(Math.round(cell.proficiency.value))
      : presentation.glyph;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`heatmap-cell-${cell.category}`}
          data-state-kind={stateKey ?? 'known'}
          data-band={cell.band}
          data-evidence={cell.evidence}
          data-recency={cell.recency}
          data-divergence={cell.divergence}
          data-source="portable_record"
          aria-label={describeCell(cell)}
          className={cn(
            'p-2 rounded-lg text-center transition-all w-full',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            presentation.tone,
          )}
        >
          <Icon className="h-4 w-4 mx-auto mb-1" aria-hidden="true" />
          <p className="text-[10px] font-medium leading-tight truncate">
            {SHORT_NAMES[cell.category] || cell.category}
          </p>
          <p className="text-sm font-bold mt-1 tabular-nums">{primary}</p>
          <p className="text-[9px] opacity-80 truncate">
            {stateKey ? presentation.label : band.label}
          </p>
          {/* Core-100 R4 (CORE-F §Provenance): the class in words on the tile itself — a manager-entered 70 and a
              demonstrated 70 never share one indistinguishable presentation. */}
          {cell.portableClass && cell.proficiency.status === 'known' && (
            <p className="text-[9px] italic truncate" data-testid={`heatmap-class-${cell.category}`} data-class={cell.portableClass}>
              {PORTABLE_CLASS_MARK[cell.portableClass]}
            </p>
          )}
          {/*
            Evidence and recency ride alongside as glyphs rather than as
            opacity. The old build dimmed sparse cells, which reads as "less
            important" rather than "less certain", and vanishes entirely for
            anyone who cannot perceive the contrast difference.
          */}
          <p className="text-[9px] opacity-70" aria-hidden="true">
            {EVIDENCE_PRESENTATION[cell.evidence].glyph}
            {' '}
            {RECENCY_PRESENTATION[cell.recency].glyph}
            {/*
              A disagreement between the portable record and the value this
              shop schedules from is marked on the tile, not buried. It is
              the kind of thing a person should be able to spot without
              opening every category one by one.
            */}
            {cell.divergence === 'differ' ? ' ≠' : ''}
          </p>
          {/*
            The shop's scheduling number, kept off the capability ramp
            entirely. It gets no band, no capability word and none of the
            band colours — it sits underneath as plain text, because it is
            operational context and the screen must not let it read as a
            statement about what this person can do.
          */}
          {cell.scheduling.status === 'known' && schedulingContext && (
            <p
              className="text-[9px] mt-1 pt-1 border-t border-current/20 opacity-80 tabular-nums"
              data-testid={`heatmap-cell-scheduling-${cell.category}`}
            >
              {schedulingContext}
            </p>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="center">
        <CellDetail
          cell={cell}
          authorityByCategory={authorityByCategory}
          hasShopContext={hasShopContext}
        />
      </PopoverContent>
    </Popover>
  );
}

export function PassportSkillHeatmap({
  technicianProfileId,
  liveShopTechnicianId,
  liveProficiencies,
  nowIso,
}: PassportSkillHeatmapProps) {
  const { cells, sources, unattributedJobRows } = useSkillHeatmapData({
    technicianProfileId,
    liveShopTechnicianId,
    liveProficiencies,
    nowIso,
  });

  // CORE-E: the score SCHEDULING consumes, and the evidence behind it.
  // Read from the canonical authority rather than from the snapshot above,
  // because explaining a mirror would describe a number nobody decides with.
  // Shop-scoped, because the authority is: with no roster row there is no
  // scheduling score to explain.
  const hasShopContext = !!liveShopTechnicianId;
  const { data: authorityByCategory } = useCategoryAuthority(
    liveShopTechnicianId,
    cells.map((c) => c.category),
  );

  const byCategory = new Map(cells.map((c) => [c.category, c]));
  const failureNotice = describeSourceFailures(failedDimensions(sources));

  return (
    <Card data-testid="passport-skill-heatmap">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Grid3X3 className="h-5 w-5 text-muted-foreground" />
          Skill Heatmap
        </CardTitle>
      </CardHeader>
      <CardContent>
        {failureNotice && (
          <p
            data-testid="heatmap-source-failure"
            role="status"
            className="mb-3 text-xs rounded-md border border-dashed border-destructive/50 bg-muted/40 p-2 text-muted-foreground"
          >
            {failureNotice}
          </p>
        )}

        <div className="space-y-4">
          {Object.entries(CATEGORY_GROUPS).map(([group, categories]) => (
            <div key={group}>
              <p className="text-xs font-medium text-muted-foreground mb-2">
                {group}
              </p>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {categories.map((category) => {
                  const cell = byCategory.get(category);
                  if (!cell) return null;
                  return (
                    <HeatmapTile
                      key={category}
                      cell={cell}
                      authorityByCategory={authorityByCategory}
                      hasShopContext={hasShopContext}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {unattributedJobRows > 0 && (
          <p
            data-testid="heatmap-unattributed-note"
            className="mt-3 text-xs text-muted-foreground"
          >
            {unattributedJobRows === 1 ? '1 record of' : `${unattributedJobRows} records of`}{' '}
            completed work could not be matched to a category. Completed-work
            counts below are a minimum, not a total.
          </p>
        )}

        <div className="mt-4 pt-4 border-t border-border space-y-3">
          <div>
            <p className="text-xs text-muted-foreground mb-2">
              Demonstrated proficiency
            </p>
            <div className="flex flex-wrap gap-2">
              {(
                ['novice', 'developing', 'capable', 'proficient', 'expert'] as const
              ).map((b) => (
                <div key={b} className="flex items-center gap-1">
                  <div className={cn('w-3 h-3 rounded', BAND_PRESENTATION[b].tone)} />
                  <span className="text-xs text-muted-foreground">
                    {BAND_PRESENTATION[b].glyph} {BAND_PRESENTATION[b].label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/*
            The absence states get their own legend row rather than being
            tucked onto the end of the capability scale — they are not a
            lower rung of it.
          */}
          <div>
            <p className="text-xs text-muted-foreground mb-2">
              When there is no value
            </p>
            <div className="flex flex-wrap gap-2">
              {(['no_evidence', 'read_failed', 'not_permitted', 'not_linked'] as const).map((k) => (
                <div key={k} className="flex items-center gap-1">
                  <div
                    className={cn('w-3 h-3 rounded', STATE_PRESENTATION[k].tone)}
                  />
                  <span className="text-xs text-muted-foreground">
                    {STATE_PRESENTATION[k].glyph} {STATE_PRESENTATION[k].label}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground flex items-start gap-1">
              <Info className="h-3 w-3 mt-0.5 shrink-0" aria-hidden="true" />
              A category with no recorded work is shown as no data. It is not
              scored as zero, and it is not a low rating.
            </p>
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-2">
              Evidence behind each rating
            </p>
            <div className="flex flex-wrap gap-2">
              {(
                // 'unknown' is listed because it is a state the screen
                // genuinely renders — it is the normal case for a shop
                // scheduling value, which carries no sample size at all.
                // Leaving it out of the legend would leave a visible marker
                // unexplained.
                ['unknown', 'none', 'sparse', 'adequate', 'strong'] as const
              ).map((e) => (
                <span key={e} className="text-xs text-muted-foreground">
                  {EVIDENCE_PRESENTATION[e].glyph} {EVIDENCE_PRESENTATION[e].label}
                </span>
              ))}
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground">
            Select any category for diagnostic competency, training readiness,
            efficiency and completed-work volume. Those are separate measures
            from demonstrated proficiency and are not combined with it.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
